import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Col, Form, Row } from "react-bootstrap";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../api/client";
import LoadingState from "../components/common/LoadingState";
import PageHeader from "../components/common/PageHeader";
import { useAuth } from "../context/AuthContext";

const initialForm = {
  destination: "",
  travel_purpose: "",
  travel_from: "",
  travel_to: "",
  no_of_days: "",
  expected_output: ""
};

function TravelRequestFormPage() {
  const history = useHistory();
  const { uuid } = useParams();
  const { user } = useAuth();
  const isEditing = Boolean(uuid);
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isEditing) {
      fetchRequest();
    }
  }, [uuid]);

  const submitLabel = useMemo(() => (isEditing ? "Update request" : "Create request"), [isEditing]);

  async function fetchRequest() {
    setLoading(true);
    setError("");

    try {
      const response = await api.get(`/travel-requests/${uuid}`);
      const request = response.data;

      setForm({
        destination: request.destination || "",
        travel_purpose: request.travel_purpose || "",
        travel_from: request.travel_from || "",
        travel_to: request.travel_to || "",
        no_of_days: request.no_of_days || "",
        expected_output: request.expected_output || ""
      });
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load travel request.");
    } finally {
      setLoading(false);
    }
  }

  function handleChange(event) {
    const { name, value } = event.target;
    setForm((current) => ({
      ...current,
      [name]: value
    }));
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setSaving(true);

    const payload = {
      user_id: Number(user?.id),
      destination: form.destination,
      travel_purpose: form.travel_purpose,
      travel_from: form.travel_from,
      travel_to: form.travel_to,
      no_of_days: form.no_of_days ? Number(form.no_of_days) : null,
      expected_output: form.expected_output,
      status: "Pending",
      approvals: []
    };

    try {
      if (isEditing) {
        await api.put(`/travel-requests/${uuid}`, payload);
        toast.success("Travel request updated");
      } else {
        await api.post("/travel-requests", payload);
        toast.success("Travel request created");
      }

      history.push("/travel-requests");
    } catch (err) {
      toast.error(err.response?.data?.error || `Failed to ${isEditing ? "update" : "create"} travel request.`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Row className="justify-content-center">
      <Col xs={12} xl={10}>
        <PageHeader
          title={isEditing ? "Edit Travel Request" : "Add Travel Request"}
          action={
            <Button variant="outline-secondary" onClick={() => history.push("/travel-requests")}>
              Back to travel requests
            </Button>
          }
        />

        {loading ? <LoadingState label="Loading travel request..." /> : null}
        {error ? <Alert variant="danger">{error}</Alert> : null}

        {!loading ? (
          <div className="content-card form-card">
            <form onSubmit={handleSubmit}>
              <Row className="g-3">
                <Col md={6}>
                  <label className="form-label">Destination</label>
                  <Form.Control name="destination" value={form.destination} onChange={handleChange} required />
                </Col>
                <Col md={6}>
                  <label className="form-label">Number of days</label>
                  <Form.Control type="number" name="no_of_days" value={form.no_of_days} onChange={handleChange} />
                </Col>
                <Col xs={12}>
                  <label className="form-label">Travel purpose</label>
                  <Form.Control as="textarea" rows={3} name="travel_purpose" value={form.travel_purpose} onChange={handleChange} required />
                </Col>
                <Col md={6}>
                  <label className="form-label">Travel Date</label>
                  <Form.Control type="date" name="travel_from" value={form.travel_from} onChange={handleChange} required />
                </Col>
                <Col md={6}>
                  <label className="form-label">Return Date</label>
                  <Form.Control type="date" name="travel_to" value={form.travel_to} onChange={handleChange} required />
                </Col>
                <Col xs={12}>
                  <label className="form-label">Expected output</label>
                  <Form.Control as="textarea" rows={3} name="expected_output" value={form.expected_output} onChange={handleChange} />
                </Col>
              </Row>

              <div className="d-flex gap-2 mt-4">
                <Button type="submit" disabled={saving}>
                  {saving ? "Saving..." : submitLabel}
                </Button>
                <Button variant="outline-secondary" onClick={() => history.push("/travel-requests")}>
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        ) : null}
      </Col>
    </Row>
  );
}

export default TravelRequestFormPage;
